"use client";

import { Button, type buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import type * as React from "react";

export interface CButtonProps
  extends React.ComponentProps<"button">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

/**
 * CButton — Design system button wrapper over shadcn Button.
 *
 * Variants: default | destructive | outline | ghost | link | icon
 * Sizes:    lg | default | sm | xs | icon | icon-sm | icon-lg
 * Extra:    loading (disables + shows spinner), leftIcon, rightIcon
 */
const CButton = ({
  loading = false,
  variant,
  leftIcon,
  rightIcon,
  children,
  disabled,
  className,
  asChild,
  ...props
}: CButtonProps) => {
  return (
    <Button
      variant={variant}
      disabled={disabled || loading}
      className={cn(
        "cursor-pointer font-normal",
        loading && "opacity-80",
        className,
      )}
      asChild={asChild}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <Loader2 className="animate-spin shrink-0" /> : leftIcon}
          {children}
          {!loading && rightIcon}
        </>
      )}
    </Button>
  );
};

export default CButton;
export { CButton };
